<template>
  <div class="floating-wrapper">
    <button
      type="button"
      class="floating-btn"
      :aria-expanded="isOpen"
      aria-controls="quick-access-panel"
      aria-label="Toggle quick access menu"
      @click="toggleMenu"
    >
      <span class="floating-btn__icon" :class="{ 'floating-btn__icon--open': isOpen }" aria-hidden="true">
        <span></span>
        <span></span>
        <span></span>
      </span>
    </button>

    <transition name="slide">
      <aside
        v-if="isOpen"
        id="quick-access-panel"
        class="menu-panel"
        aria-label="Quick access menu"
      >
        <div class="menu-panel__header">
          <p class="menu-panel__eyebrow">Quick Access</p>
          <h2 class="menu-panel__title">Useful Links</h2>
        </div>

        <nav class="menu-links" aria-label="External links">
          <a
            v-for="link in quickLinks"
            :key="link.title"
            :href="link.href"
            target="_blank"
            rel="noopener noreferrer"
            class="menu-link"
            :class="`menu-link--${link.tone}`"
          >
            <span class="menu-link__icon">{{ link.short }}</span>

            <span class="menu-link__content">
              <span class="menu-link__title">{{ link.title }}</span>
              <span class="menu-link__meta">{{ link.meta }}</span>
            </span>

            <span class="menu-link__arrow">></span>
          </a>
        </nav>

        <div class="menu-panel__divider"></div>

        <section class="contact-card" aria-labelledby="floating-contact-title">
          <p class="contact-card__eyebrow">Contact</p>
          <h3 id="floating-contact-title" class="contact-card__title">PT Pupuk Indonesia Utilitas</h3>

          <div class="contact-card__list">
            <div class="contact-card__item">
              <span class="contact-card__badge">PH</span>
              <div>
                <span class="contact-card__label">Phone</span>
                <a class="contact-card__value" href="tel:+622129661630">(021) 29661630</a>
              </div>
            </div>

            <div class="contact-card__item">
              <span class="contact-card__badge">EM</span>
              <div>
                <span class="contact-card__label">Email</span>
                <a class="contact-card__value" href="mailto:info@pi-utilitas.com">info@pi-utilitas.com</a>
              </div>
            </div>

            <div class="contact-card__item contact-card__item--address">
              <span class="contact-card__badge">AD</span>
              <div>
                <span class="contact-card__label">Office</span>
                <p class="contact-card__value">
                  PT Pupuk Indonesia Utilitas<br>
                  Gedung ROB 1 Lt. 5<br>
                  Jl. Kalibata Timur I No. 36<br>
                  Jakarta Selatan, 12740
                </p>
              </div>
            </div>
          </div>
        </section>
      </aside>
    </transition>
  </div>
</template>

<script setup>
import { ref } from 'vue'

const isOpen = ref(false)

const quickLinks = [
  {
    title: 'E-Procurement',
    meta: 'Open supplier portal',
    short: 'EP',
    tone: 'orange',
    href: 'https://eprocurement.pupuk-indonesia.com/beranda'
  },
  {
    title: 'Whistleblowing System',
    meta: 'Secure reporting access',
    short: 'WB',
    tone: 'gold',
    href: 'https://wbs.pupuk-indonesia.com/'
  },
  {
    title: 'E-Announcement LHKPN',
    meta: 'Public disclosure page',
    short: 'LK',
    tone: 'teal',
    href: 'https://elhkpn.kpk.go.id/portal/user/pengumuman_lhkpn/VERWVFpGZHhlVU5TUml0VVJGbDBZMlJOTmt4NVYzbHJkR3hRVnpkR2FURjJWM2NyZDJWWE1WQm5Oa3hIVjI1eGNERkNhRWRDY1ZkcFlsb3pPVzR6T1E9PQ=='
  }
]

function toggleMenu() {
  isOpen.value = !isOpen.value
}
</script>

<style scoped>
.floating-wrapper {
  position: fixed;
  top: 50%;
  right: clamp(10px, 2vw, 22px);
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  gap: 14px;
  z-index: 9998;
}

.floating-btn {
  width: 58px;
  height: 58px;
  border: 1px solid rgba(255, 255, 255, 0.34);
  border-radius: 20px;
  background: linear-gradient(180deg, #0c6abc 0%, #084e8b 100%);
  box-shadow: 0 20px 36px rgba(6, 23, 39, 0.28), inset 0 1px 0 rgba(255, 255, 255, 0.2);
  color: #ffffff;
  cursor: pointer;
  display: grid;
  place-items: center;
  transition: transform 0.22s ease, box-shadow 0.22s ease, background 0.22s ease;
}

.floating-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 24px 40px rgba(6, 23, 39, 0.32), inset 0 1px 0 rgba(255, 255, 255, 0.22);
}

.floating-btn:focus-visible {
  outline: 2px solid #f15a24;
  outline-offset: 3px;
}

.floating-btn__icon {
  position: relative;
  display: block;
  width: 20px;
  height: 16px;
}

.floating-btn__icon span {
  position: absolute;
  left: 0;
  width: 20px;
  height: 2px;
  border-radius: 999px;
  background: #ffffff;
  transition: transform 0.22s ease, top 0.22s ease, opacity 0.18s ease;
}

.floating-btn__icon span:nth-child(1) {
  top: 0;
}

.floating-btn__icon span:nth-child(2) {
  top: 7px;
}

.floating-btn__icon span:nth-child(3) {
  top: 14px;
}

.floating-btn__icon--open span:nth-child(1) {
  top: 7px;
  transform: rotate(45deg);
}

.floating-btn__icon--open span:nth-child(2) {
  opacity: 0;
}

.floating-btn__icon--open span:nth-child(3) {
  top: 7px;
  transform: rotate(-45deg);
}

.menu-panel {
  width: min(340px, calc(100vw - 96px));
  max-height: min(78vh, 640px);
  overflow-y: auto;
  padding: 22px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 28px;
  background: linear-gradient(180deg, rgba(10, 92, 160, 0.97), rgba(5, 56, 98, 0.98));
  color: #ffffff;
  box-shadow: 0 28px 56px rgba(4, 21, 36, 0.34);
  backdrop-filter: blur(18px);
}

.menu-panel__header {
  margin-bottom: 18px;
}

.menu-panel__eyebrow,
.contact-card__eyebrow,
.contact-card__label {
  display: block;
  margin: 0;
  color: rgba(255, 255, 255, 0.68);
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.menu-panel__title {
  margin: 8px 0 0;
  font-size: 1.45rem;
  font-weight: 700;
  line-height: 1.15;
}

.menu-links {
  display: grid;
  gap: 12px;
}

.menu-link {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.08);
  color: #ffffff;
  text-decoration: none;
  transition: transform 0.22s ease, background-color 0.22s ease, border-color 0.22s ease, box-shadow 0.22s ease;
}

.menu-link:hover {
  transform: translateX(-4px);
  background: rgba(255, 255, 255, 0.12);
  border-color: rgba(255, 255, 255, 0.2);
  box-shadow: 0 18px 28px rgba(4, 21, 36, 0.18);
}

.menu-link__icon {
  width: 44px;
  height: 44px;
  flex: 0 0 44px;
  display: grid;
  place-items: center;
  border-radius: 14px;
  font-size: 0.76rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  color: #ffffff;
  border: 1px solid rgba(255, 255, 255, 0.16);
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.24), rgba(255, 255, 255, 0.08));
}

.menu-link--orange .menu-link__icon {
  background: linear-gradient(135deg, #ff9564, #f15a24);
}

.menu-link--gold .menu-link__icon {
  background: linear-gradient(135deg, #ffd773, #f3a800);
}

.menu-link--teal .menu-link__icon {
  background: linear-gradient(135deg, #4ed6c1, #109270);
}

.menu-link__content {
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.menu-link__title {
  font-size: 1.02rem;
  font-weight: 700;
  line-height: 1.25;
}

.menu-link__meta {
  margin-top: 4px;
  color: rgba(255, 255, 255, 0.72);
  font-size: 0.77rem;
  line-height: 1.3;
}

.menu-link__arrow {
  width: 34px;
  height: 34px;
  flex: 0 0 34px;
  display: grid;
  place-items: center;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.88);
  font-size: 1rem;
  font-weight: 700;
}

.menu-panel__divider {
  height: 1px;
  margin: 20px 0 18px;
  background: linear-gradient(90deg, rgba(255, 255, 255, 0.12), rgba(255, 255, 255, 0.45), rgba(255, 255, 255, 0.12));
}

.contact-card {
  padding: 18px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.08);
}

.contact-card__title {
  margin: 10px 0 0;
  font-size: 1.18rem;
  font-weight: 700;
  line-height: 1.25;
}

.contact-card__list {
  display: grid;
  gap: 14px;
  margin-top: 18px;
}

.contact-card__item {
  display: grid;
  grid-template-columns: 40px 1fr;
  gap: 12px;
  align-items: start;
}

.contact-card__badge {
  width: 40px;
  height: 40px;
  display: grid;
  place-items: center;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.1);
  color: #ffffff;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.contact-card__label {
  margin-bottom: 5px;
}

.contact-card__value {
  margin: 0;
  color: #ffffff;
  font-size: 0.96rem;
  line-height: 1.55;
  text-decoration: none;
}

a.contact-card__value:hover {
  color: #d7ecff;
}

.slide-enter-active,
.slide-leave-active {
  transition: transform 0.28s ease, opacity 0.28s ease;
}

.slide-enter-from,
.slide-leave-to {
  transform: translateX(18px);
  opacity: 0;
}

@media (max-width: 640px) {
  .floating-wrapper {
    right: 10px;
    gap: 10px;
  }

  .floating-btn {
    width: 52px;
    height: 52px;
    border-radius: 18px;
  }

  .menu-panel {
    width: min(310px, calc(100vw - 76px));
    padding: 18px;
    border-radius: 24px;
  }

  .menu-link {
    padding: 12px 13px;
    gap: 12px;
  }

  .menu-link__title {
    font-size: 0.96rem;
  }

  .menu-link__meta {
    font-size: 0.72rem;
  }

  .contact-card {
    padding: 16px;
  }

  .contact-card__value {
    font-size: 0.9rem;
  }
}
</style>
